import { M3, MDL } from '@messenger/consts'

type ThemeLike =
  | { application?: string | null; id?: string; theme_id?: string }
  | null
  | undefined

type OrderProductLike = { product_id?: string | null } | null | undefined

type CatalogProductLike = {
  id?: string
  source?: string | null
}

// A theme's app type. Themes flagged M3 belong to M3; everything else
// (MDL, null, or a missing `application`) is treated as MDL — matching the
// existing badge logic (`card.application === M3 ? 'M3' : 'MDL'`).
export const getThemeAppType = (theme: ThemeLike): string =>
  theme?.application === M3 ? M3 : MDL

// The app type(s) an order actually spans, derived from its order products'
// source tables. Admins always work in the MDL editor, so a URL-derived
// applicationType stays MDL even on master orders carrying M3 products —
// product source is the truth, not the order's applicationType (#2482).
// `GET /v1/products` returns the MDL+M3 catalog union where each row carries
// a `source: 'mdl' | 'm3'` discriminator; order products point at either
// table via `product_id`.
export const getOrderProductAppTypes = (
  orderProducts: OrderProductLike[] | null | undefined,
  catalogProducts: CatalogProductLike[] | null | undefined,
): string[] => {
  if (!orderProducts?.length || !catalogProducts?.length) return []
  const appTypes = new Set<string>()
  for (const orderProduct of orderProducts) {
    if (!orderProduct?.product_id) continue
    const product = catalogProducts.find(
      (catalogProduct) => catalogProduct?.id === orderProduct.product_id,
    )
    if (!product?.source) continue
    appTypes.add(product.source === 'm3' ? M3 : MDL)
  }
  return [...appTypes]
}

// True when the theme belongs to none of the app types the order spans.
// A null/undefined theme (deselect) is never a mismatch, and an empty
// applicationTypes list (order products not resolvable yet) never warns.
export const isCrossAppTheme = (
  theme: ThemeLike,
  applicationTypes: string[],
): boolean => {
  if (!theme) return false
  if (!applicationTypes?.length) return false
  return !applicationTypes.includes(getThemeAppType(theme))
}
